import api from '../../../../services/api';
import logger from '../../../../utils/logging/logger';

// The post-fetch bulk-pricing calculation for a freshly-fetched multi-course
// cart, extracted VERBATIM from useCourseProductFetch's fetchData. The
// original's early `return;` statements exited fetchData entirely, but since
// this block was the last thing in the `else if (courseIds)` branch (nothing
// else ran afterward), returning from this function instead is equivalent —
// the caller has nothing left to do either way.
export const calculateInitialBulkPricing = async ({
  coursesData,
  productIds,
  paramsKey,
  apiCallTimestampsRef,
  RATE_LIMIT_DELAY,
  abortControllerRef,
  prevBulkPricingRef,
  setBulkPricing,
  setBulkPricingLoading,
  paymentApi = api,
}) => {
  // FIX #14: Wait for bulk pricing before creating initial payment intent
  // FIX #35: Only calculate bulk pricing when needed (more than 1 course)
  // Check for bulk pricing if more than 1 course
  // Do this after setting courses to ensure we have the latest course data
  // FIX: Only calculate bulk pricing for courses, not products
  // Products should not use bulk pricing - they use their own pricing logic
  if (coursesData.length > 1 && !productIds) {
    try {
      // Ensure course IDs are integers (backend expects integers)
      const courseIdsForBulk = coursesData.map(course => parseInt(course.id));
      // FIX #31: Rate limiting - prevent rapid API calls
      const now = Date.now();
      const lastCallTime = apiCallTimestampsRef.current.calculateBulkPricing;
      if (lastCallTime && (now - lastCallTime) < RATE_LIMIT_DELAY) {
        logger.debug('Bulk pricing calculation rate limited, skipping');
        // Still set loading to false since we're skipping
        setBulkPricingLoading(false);
        return;
      }
      apiCallTimestampsRef.current.calculateBulkPricing = now;

      logger.debug('Calculating bulk pricing', {
        courseIdsForBulk,
        courseCount: courseIdsForBulk.length,
        courses: coursesData.map(c => ({ id: c.id, name: c.name, price: c.price }))
      });
      const bulkResponse = await paymentApi.calculateBulkPricing(courseIdsForBulk);

      // FIX #13: Check again if component is still mounted before setting state
      if (abortControllerRef.current?.signal.aborted) {
        logger.debug('Bulk pricing response received but component unmounted - ignoring');
        return;
      }

      logger.debug('Bulk pricing response', {
        success: bulkResponse.success,
        hasBulkPricing: !!bulkResponse.bulkPricing,
        bulkPricing: bulkResponse.bulkPricing,
        pricing: bulkResponse.pricing,
        message: bulkResponse.message
      });
      if (bulkResponse.success && bulkResponse.bulkPricing) {
        // FIX #13: Check again before setting state
        if (!abortControllerRef.current?.signal.aborted) {
          // CRITICAL: Not restoring - this is a fresh calculation, so update prevRef
          prevBulkPricingRef.current = bulkResponse;
          setBulkPricing(bulkResponse);
          // Store bulk pricing in sessionStorage for future remounts
          if (typeof sessionStorage !== 'undefined') {
            const bulkPricingKey = `paymentForm_bulkPricing_${paramsKey}`;
            sessionStorage.setItem(bulkPricingKey, JSON.stringify(bulkResponse));
          }
          logger.info('Bulk pricing applied successfully', {
            discount: bulkResponse.pricing.discountAmount,
            originalPrice: bulkResponse.pricing.originalPrice,
            finalPrice: bulkResponse.pricing.finalPrice,
            savings: bulkResponse.pricing.savings,
            ruleName: bulkResponse.bulkPricing.name
          });
        }
      } else {
        logger.warn('No bulk pricing available', {
          response: bulkResponse,
          message: bulkResponse.message || 'No bulk pricing rule found'
        });
        // FIX #13: Only set state if component is still mounted
        if (!abortControllerRef.current?.signal.aborted) {
          setBulkPricing(null); // Explicitly clear bulk pricing if not available
        }
      }
    } catch (bulkError) {
      logger.error('Error calculating bulk pricing', {
        bulkError,
        errorMessage: bulkError.message,
        errorStack: bulkError.stack
      });
      // FIX #13: Only set state if component is still mounted
      if (!abortControllerRef.current?.signal.aborted) {
        setBulkPricing(null); // Clear bulk pricing on error
      }
    } finally {
      // FIX #6 & #14: Clear loading state (only if component is still mounted)
      if (!abortControllerRef.current?.signal.aborted) {
        setBulkPricingLoading(false);
      }
    }
  } else {
    // FIX #35: Clear bulk pricing if only one course (no need to call API)
    setBulkPricing(null);
    setBulkPricingLoading(false);
  }
};
